"use client"

import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "util/cn"
import {
  SurfaceLayerProvider,
  useResolvedSurfaceLayer,
} from "ui/layers"

interface DrawerContentProps extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  title?: string;
  titlehidden?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  stickyFooter?: boolean;
  hideHandle?: boolean;
  fullScreen?: boolean;
  /** Preferred explicit base layer for this drawer surface. */
  layerBase?: number;
  /** Backward-compatible alias for layerBase. */
  zIndex?: number;
  /**
   * Optional id of the `<form>` element rendered inside `children`.
   * Footer submit buttons in `FormActions` pair to it via
   * `<button form={formId} type="submit">` so browser-native Enter
   * submits the form even though the button lives in the drawer footer.
   */
  formId?: string;
}

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
Drawer.displayName = "Drawer"

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, style, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 bg-black/50", className)}
    style={style}
    data-eoe-surface-overlay="true"
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & DrawerContentProps
>(({ className, children, footer, stickyFooter = true, hideHandle = false, fullScreen = false, layerBase, zIndex, formId, style, ...props }, ref) => {
  const surfaceId = React.useId()
  const [contentElement, setContentElement] = React.useState<HTMLElement | null>(null)
  const layer = useResolvedSurfaceLayer({
    tier: "drawer",
    layerBase: layerBase ?? zIndex,
  })
  const providedLayer = React.useMemo(
    () => ({ ...layer, contentElement }),
    [contentElement, layer]
  )

  return (
    <DrawerPortal>
      <DrawerOverlay
        style={{ zIndex: layer.overlay }}
        data-eoe-surface-root="true"
        data-eoe-surface-id={surfaceId}
        data-eoe-surface-type="drawer"
      />
      <DrawerPrimitive.Content
        ref={(node) => {
          setContentElement(node)
          if (typeof ref === "function") {
            ref(node)
          } else if (ref) {
            ref.current = node
          }
        }}
        className={cn(
          fullScreen
            ? "fixed inset-0 flex h-full flex-col border-none bg-eoe-bg dark:bg-eoe-dark-bg-modal text-eoe-text-sub dark:text-eoe-dark-text-sub"
            : "fixed inset-x-0 bottom-0 min-h-[10vh] max-h-[90vh] mt-24 flex h-auto flex-col rounded-t-2xl border-none bg-eoe-bg dark:bg-eoe-dark-bg-modal text-eoe-text-sub dark:text-eoe-dark-text-sub",
          className
        )}
        style={{ ...(style ?? {}), zIndex: layer.base }}
        onClick={(e) => e.stopPropagation()}
        data-eoe-surface-root="true"
        data-eoe-surface-type="drawer"
        data-eoe-surface-id={surfaceId}
        data-eoe-layer-base={layer.base}
        data-form-id={formId}
        {...props}
      >
        <SurfaceLayerProvider value={providedLayer}>
          {/* Fixed Header: Handle + Title */}
          <div className="relative z-10 flex-shrink-0 cursor-pointer">
            {!hideHandle && (
              <div className="my-3 flex w-full cursor-pointer flex-col items-center justify-center hover:cursor-pointer">
                <div className="mx-auto h-0.5 w-[60px] items-center justify-center rounded-full bg-eoe-border-main hover:cursor-pointer" />
              </div>
            )}
            {props.title && (
              <div className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 text-center text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <DrawerTitle className={`my-3 flex px-1 text-eoe-text-sub dark:text-eoe-dark-text-sub ${props.titlehidden ? "hidden" : ""}`}>
                  {props.title}
                </DrawerTitle>
                <div className={`mx-auto h-[0.25px] w-full rounded-full bg-eoe-border-main opacity-50 dark:bg-eoe-dark-border-main ${props.titlehidden ? "hidden" : ""}`} />
              </div>
            )}
            {/* header & title */}
            {props.header && (
              <div className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 text-center text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {props.header}
              </div>
            )}
          </div>

          {/* Scrollable Content */}
          <div
            className={`flex-1 overflow-y-auto overflow-x-hidden ${footer && stickyFooter ? "pb-0" : "pb-1"}`}
            data-eoe-surface-content="true"
          >
            {children}
          </div>

          {/* Sticky Footer */}
          {footer && stickyFooter && (
            <div className="flex-shrink-0 bg-eoe-bg dark:bg-eoe-dark-bg-modal">
              {footer}
            </div>
          )}

          {/* Non-sticky Footer (added to content flow) */}
          {footer && !stickyFooter && footer}
        </SurfaceLayerProvider>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
})
DrawerContent.displayName = "DrawerContent"

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-md font-semibold leading-none tracking-tight text-center",
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
